import { blupayApi } from "../api/client";

/**
 * Staff money-operations endpoints.
 *
 * These live outside `/admin/*` (they are the same routes the operations team
 * already used via API clients) and are gated by `require_roles(OPERATIONS)`
 * on the backend; the `admin` role passes every role check, so the admin
 * portal can call them directly.
 *
 * Everything here can move or finalize customer money. The UI gates these
 * behind `transfers:execute`, but the backend is the real enforcement point.
 */
export const operationsService = {
  /**
   * Full staff view of a transfer: legs, stablecoin settlements, and the
   * immutable status-event timeline.
   */
  async getTransferDetail(transferId) {
    const { data } = await blupayApi.get(`/transfers/${transferId}/detail`);
    return data;
  },

  /** Submit a pending leg to its provider. */
  async executeLeg(legId) {
    const { data } = await blupayApi.post(
      `/provider-execution/legs/${legId}/execute`
    );
    return data;
  },

  /**
   * Force a leg terminal-complete. Admin-only on the backend: this asserts an
   * outcome the provider has not confirmed through a webhook, so it should
   * only follow out-of-band evidence.
   */
  async markLegCompleted(legId, payload = {}) {
    const { data } = await blupayApi.post(
      `/provider-execution/legs/${legId}/completed`,
      payload
    );
    return data;
  },

  /** Force a leg terminal-failed. Admin-only on the backend. */
  async markLegFailed(legId, payload = {}) {
    const { data } = await blupayApi.post(
      `/provider-execution/legs/${legId}/failed`,
      payload
    );
    return data;
  },

  /**
   * Record operator-verified prefunding for a cross-provider settlement leg,
   * which releases the offramp.
   */
  async confirmSettlementFunded(legId, payload = {}) {
    const { data } = await blupayApi.post(
      `/provider-execution/legs/${legId}/settlement-funded`,
      payload
    );
    return data;
  },

  /**
   * Resume a transfer whose AML funding hold has elapsed.
   *
   * This cannot override an active hold - the backend refuses with a 409 and
   * the time it becomes available. The reason is recorded in the audit log.
   */
  async resumeFundingHold(transferId, payload = {}) {
    const { data } = await blupayApi.post(
      `/transfers/${transferId}/release-funding-hold`,
      payload
    );
    return data;
  },

  /** Sweep every Bridge onramp still sitting in `processing`. */
  async reconcileBridgeOnramps() {
    const { data } = await blupayApi.post("/transfers/reconcile/bridge-onramps");
    return data;
  },

  /** Sweep every Zuba onramp still sitting in `processing`. */
  async reconcileZubaOnramps() {
    const { data } = await blupayApi.post(
      "/provider-execution/reconcile-zuba-onramps"
    );
    return data;
  },
};
